import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import CodeIcon from "@mui/icons-material/Code";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";

export const RichTextEditor = ({
  value = "",
  onChange,
  placeholder = "Write your email message here... You can format text, add headings, insert links and images.",
  minHeight = "280px",
}) => {
  const editorRef = useRef(null);
  const [viewMode, setViewMode] = useState("visual"); // 'visual' | 'html' | 'preview'
  const [rawHtml, setRawHtml] = useState(value || "");

  // Link Dialog State
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");

  // Image Dialog State
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageAlt, setImageAlt] = useState("");

  // Keep editor DOM in sync with value when external changes happen
  useEffect(() => {
    if (editorRef.current && viewMode === "visual") {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || "";
      }
    }
    setRawHtml(value || "");
  }, [value, viewMode]);

  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      setRawHtml(html);
      if (onChange) onChange(html);
    }
  };

  const executeCommand = (command, val = null) => {
    if (viewMode !== "visual") return;
    if (editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand(command, false, val);
    handleInput();
  };

  const handleFormatBlock = (blockTag) => {
    executeCommand("formatBlock", blockTag);
  };

  // Insert Link Handling
  const handleOpenLinkDialog = () => {
    const selected = window.getSelection() ? window.getSelection().toString() : "";
    setLinkText(selected);
    setLinkUrl("");
    setLinkDialogOpen(true);
  };

  const handleConfirmLink = () => {
    if (!linkUrl) return;
    if (editorRef.current) editorRef.current.focus();
    
    let url = linkUrl.trim();
    if (!url.startsWith("http://") && !url.startsWith("https://") && !url.startsWith("mailto:")) {
      url = "https://" + url;
    }

    if (linkText && !window.getSelection()?.toString()) {
      executeCommand(
        "insertHTML",
        `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #094B38; text-decoration: underline; font-weight: 600;">${linkText}</a>`
      );
    } else {
      executeCommand("createLink", url);
    }
    setLinkDialogOpen(false);
  };

  // Insert Image Handling
  const handleOpenImageDialog = () => {
    setImageUrl("");
    setImageAlt("");
    setImageDialogOpen(true);
  };

  const handleConfirmImage = () => {
    if (!imageUrl) return;
    if (editorRef.current) editorRef.current.focus();

    const imgHtml = `<img src="${imageUrl.trim()}" alt="${imageAlt || 'Image'}" style="max-width: 100%; height: auto; border-radius: 8px; margin: 12px 0; display: block;" />`;
    executeCommand("insertHTML", imgHtml);
    setImageDialogOpen(false);
  };

  const handleRawHtmlChange = (e) => {
    const val = e.target.value;
    setRawHtml(val);
    if (onChange) onChange(val);
  };

  return (
    <Box
      sx={{
        border: "1px solid #CBD5E1",
        borderRadius: 2.5,
        bgcolor: "#FFFFFF",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        "&:focus-within": {
          borderColor: "#154C3A",
          boxShadow: "0 0 0 2px rgba(21, 76, 58, 0.15)",
        },
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.5,
          p: 1,
          bgcolor: "#F8FAFC",
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        {/* Paragraph Format Selector */}
        <Select
          size="small"
          defaultValue="p"
          onChange={(e) => handleFormatBlock(e.target.value)}
          disabled={viewMode !== "visual"}
          sx={{
            height: 32,
            fontSize: "13px",
            fontWeight: 600,
            bgcolor: "#FFFFFF",
            "& .MuiSelect-select": { py: 0.5, px: 1.5 },
          }}
        >
          <MenuItem value="p">Normal Text</MenuItem>
          <MenuItem value="h1" sx={{ fontSize: "18px", fontWeight: 700 }}>
            Heading 1
          </MenuItem>
          <MenuItem value="h2" sx={{ fontSize: "16px", fontWeight: 700 }}>
            Heading 2
          </MenuItem>
          <MenuItem value="h3" sx={{ fontSize: "14px", fontWeight: 700 }}>
            Heading 3
          </MenuItem>
        </Select>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        {/* Basic Styles */}
        <Tooltip title="Bold (Ctrl+B)">
          <span>
            <IconButton
              size="small"
              onClick={() => executeCommand("bold")}
              disabled={viewMode !== "visual"}
              sx={{ color: "#334155" }}
            >
              <FormatBoldIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Italic (Ctrl+I)">
          <span>
            <IconButton
              size="small"
              onClick={() => executeCommand("italic")}
              disabled={viewMode !== "visual"}
              sx={{ color: "#334155" }}
            >
              <FormatItalicIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Underline (Ctrl+U)">
          <span>
            <IconButton
              size="small"
              onClick={() => executeCommand("underline")}
              disabled={viewMode !== "visual"}
              sx={{ color: "#334155" }}
            >
              <FormatUnderlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Strikethrough">
          <span>
            <IconButton
              size="small"
              onClick={() => executeCommand("strikeThrough")}
              disabled={viewMode !== "visual"}
              sx={{ color: "#334155" }}
            >
              <StrikethroughSIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        {/* Lists */}
        <Tooltip title="Bullet List">
          <span>
            <IconButton
              size="small"
              onClick={() => executeCommand("insertUnorderedList")}
              disabled={viewMode !== "visual"}
              sx={{ color: "#334155" }}
            >
              <FormatListBulletedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Numbered List">
          <span>
            <IconButton
              size="small"
              onClick={() => executeCommand("insertOrderedList")}
              disabled={viewMode !== "visual"}
              sx={{ color: "#334155" }}
            >
              <FormatListNumberedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        {/* Alignments */}
        <Tooltip title="Align Left">
          <span>
            <IconButton
              size="small"
              onClick={() => executeCommand("justifyLeft")}
              disabled={viewMode !== "visual"}
              sx={{ color: "#334155" }}
            >
              <FormatAlignLeftIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Align Center">
          <span>
            <IconButton
              size="small"
              onClick={() => executeCommand("justifyCenter")}
              disabled={viewMode !== "visual"}
              sx={{ color: "#334155" }}
            >
              <FormatAlignCenterIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Align Right">
          <span>
            <IconButton
              size="small"
              onClick={() => executeCommand("justifyRight")}
              disabled={viewMode !== "visual"}
              sx={{ color: "#334155" }}
            >
              <FormatAlignRightIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Justify">
          <span>
            <IconButton
              size="small"
              onClick={() => executeCommand("justifyFull")}
              disabled={viewMode !== "visual"}
              sx={{ color: "#334155" }}
            >
              <FormatAlignJustifyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        {/* Media & Links */}
        <Tooltip title="Insert Link">
          <span>
            <IconButton
              size="small"
              onClick={handleOpenLinkDialog}
              disabled={viewMode !== "visual"}
              sx={{ color: "#2563EB" }}
            >
              <InsertLinkIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Insert Image URL">
          <span>
            <IconButton
              size="small"
              onClick={handleOpenImageDialog}
              disabled={viewMode !== "visual"}
              sx={{ color: "#059669" }}
            >
              <AddPhotoAlternateIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Blockquote">
          <span>
            <IconButton
              size="small"
              onClick={() => executeCommand("formatBlock", "blockquote")}
              disabled={viewMode !== "visual"}
              sx={{ color: "#334155" }}
            >
              <FormatQuoteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Horizontal Divider Line">
          <span>
            <IconButton
              size="small"
              onClick={() => executeCommand("insertHorizontalRule")}
              disabled={viewMode !== "visual"}
              sx={{ color: "#334155" }}
            >
              <HorizontalRuleIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Clear Formatting">
          <span>
            <IconButton
              size="small"
              onClick={() => executeCommand("removeFormat")}
              disabled={viewMode !== "visual"}
              sx={{ color: "#64748B" }}
            >
              <FormatClearIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {/* View Mode Toggle */}
        <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, newMode) => {
              if (newMode) setViewMode(newMode);
            }}
            sx={{ height: 30 }}
          >
            <ToggleButton value="visual" sx={{ px: 1, py: 0, textTransform: "none", fontSize: "12px", fontWeight: 600 }}>
              <EditIcon sx={{ fontSize: 14, mr: 0.5 }} /> Visual
            </ToggleButton>
            <ToggleButton value="html" sx={{ px: 1, py: 0, textTransform: "none", fontSize: "12px", fontWeight: 600 }}>
              <CodeIcon sx={{ fontSize: 14, mr: 0.5 }} /> HTML
            </ToggleButton>
            <ToggleButton value="preview" sx={{ px: 1, py: 0, textTransform: "none", fontSize: "12px", fontWeight: 600 }}>
              <VisibilityIcon sx={{ fontSize: 14, mr: 0.5 }} /> Preview
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Editor Content Area */}
      {viewMode === "visual" && (
        <Box
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onBlur={handleInput}
          data-placeholder={placeholder}
          sx={{
            minHeight,
            maxHeight: "500px",
            overflowY: "auto",
            p: 2.5,
            outline: "none",
            fontSize: "15px",
            lineHeight: 1.7,
            color: "#1E293B",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            "&:empty:before": {
              content: "attr(data-placeholder)",
              color: "#94A3B8",
              cursor: "text",
            },
            "& blockquote": {
              borderLeft: "4px solid #059669",
              bgcolor: "#F0FDF4",
              p: "8px 16px",
              my: 1.5,
              borderRadius: "0 6px 6px 0",
              color: "#166534",
              fontStyle: "italic",
            },
            "& hr": {
              border: "none",
              borderTop: "1px solid #E2E8F0",
              my: 2,
            },
            "& ul, & ol": {
              pl: 3,
              my: 1,
            },
            "& img": {
              maxWidth: "100%",
              height: "auto",
              borderRadius: 2,
              my: 1.5,
            },
            "& a": {
              color: "#094B38",
              textDecoration: "underline",
              fontWeight: 600,
            },
            "& h1": { fontSize: "22px", fontWeight: 700, my: 1.5, color: "#0F172A" },
            "& h2": { fontSize: "18px", fontWeight: 700, my: 1.2, color: "#1E293B" },
            "& h3": { fontSize: "16px", fontWeight: 600, my: 1, color: "#334155" },
            "& p": { my: 0.8 },
          }}
        />
      )}

      {viewMode === "html" && (
        <TextField
          multiline
          fullWidth
          value={rawHtml}
          onChange={handleRawHtmlChange}
          placeholder="Edit raw HTML code..."
          InputProps={{
            sx: {
              fontFamily: "monospace",
              fontSize: "13px",
              bgcolor: "#0F172A",
              color: "#38BDF8",
              p: 2,
              borderRadius: 0,
              minHeight,
            },
          }}
        />
      )}

      {viewMode === "preview" && (
        <Box
          sx={{
            minHeight,
            maxHeight: "500px",
            overflowY: "auto",
            p: 3,
            bgcolor: "#F8FAFC",
          }}
        >
          <Box
            sx={{
              maxWidth: 650,
              mx: "auto",
              bgcolor: "#FFFFFF",
              p: 3,
              borderRadius: 3,
              border: "1px solid #E2E8F0",
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
            }}
          >
            <Box sx={{ borderBottom: "2px solid #094B38", pb: 1.5, mb: 2.5 }}>
              <Typography variant="h6" fontWeight={700} color="#094B38">
                MindSherpa Announcement
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Official Message from MindSherpa
              </Typography>
            </Box>

            <Box
              dangerouslySetInnerHTML={{ __html: rawHtml || "<p style='color: #94A3B8; font-style: italic;'>[Empty email body]</p>" }}
              sx={{
                fontSize: "15px",
                lineHeight: 1.7,
                color: "#1E293B",
                "& blockquote": {
                  borderLeft: "4px solid #059669",
                  bgcolor: "#F0FDF4",
                  p: "8px 16px",
                  my: 1.5,
                  borderRadius: "0 6px 6px 0",
                  color: "#166534",
                },
                "& img": {
                  maxWidth: "100%",
                  borderRadius: 2,
                  my: 1.5,
                },
                "& a": {
                  color: "#094B38",
                  textDecoration: "underline",
                  fontWeight: 600,
                },
              }}
            />

            <Box sx={{ borderTop: "1px solid #E2E8F0", pt: 2, mt: 3, textAlign: "center" }}>
              <Typography variant="caption" color="#94A3B8">
                Sent by MindSherpa &bull; mindsherpa.health
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Insert Link Dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#154C3A" }}>Insert Link</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Link Text (optional)"
            placeholder="e.g. Visit MindSherpa"
            fullWidth
            size="small"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
          />
          <TextField
            label="URL Address"
            placeholder="https://mindsherpa.health/..."
            fullWidth
            required
            size="small"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#F8FAFC" }}>
          <Button onClick={() => setLinkDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLink}
            disabled={!linkUrl}
            variant="contained"
            sx={{ bgcolor: "#154C3A", textTransform: "none", fontWeight: 600 }}
          >
            Insert Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Insert Image Dialog */}
      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#154C3A" }}>Insert Image</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Image URL Address"
            placeholder="https://images.unsplash.com/..."
            fullWidth
            required
            size="small"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
          />
          <TextField
            label="Alt Description (optional)"
            placeholder="e.g. Session Banner"
            fullWidth
            size="small"
            value={imageAlt}
            onChange={(e) => setImageAlt(e.target.value)}
          />
          {imageUrl && (
            <Box sx={{ mt: 1, textAlign: "center", maxHeight: 150, overflow: "hidden", borderRadius: 2, border: "1px solid #E2E8F0" }}>
              <img src={imageUrl} alt="Preview" style={{ maxWidth: "100%", maxHeight: 150, objectFit: "contain" }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#F8FAFC" }}>
          <Button onClick={() => setImageDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmImage}
            disabled={!imageUrl}
            variant="contained"
            sx={{ bgcolor: "#154C3A", textTransform: "none", fontWeight: 600 }}
          >
            Insert Image
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
